import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiRepository } from '../../../../shared/infrastructure/api.repository';
import { User } from '../../domain/entities/user.entity';
import {
  UserRepositoryPort,
  UserListPagedRequest,
  UserListPagedResponse,
  CreateUserRequest,
  EditUserRequest,
  EditUserIdentityRequest,
  EditPasswordByAdminRequest,
  EditPasswordByOwnUserRequest,
  EditTemporalPasswordRequest,
} from '../../domain/repositories/user.repository.port';
import { UserMapper, UserApiResponse, UserListPagedApiResponse } from '../mappers/user.mapper';

@Injectable({ providedIn: 'root' })
export class UserRepository extends ApiRepository implements UserRepositoryPort {
  private readonly baseUrl = '/users';

  create(request: CreateUserRequest): Observable<number> {
    return this.post<number, CreateUserRequest>(
      `${this.baseUrl}/Create`,
      request
    );
  }

  remove(id: string): Observable<void> {
    return this.delete(`${this.baseUrl}/Delete/${id}`);
  }

  edit(id: string, request: EditUserRequest): Observable<void> {
    return this.put<void, EditUserRequest>(
      `${this.baseUrl}/Edit/${id}`,
      request
    );
  }

  editIdentity(id: string, request: EditUserIdentityRequest): Observable<void> {
    return this.put<void, EditUserIdentityRequest>(
      `${this.baseUrl}/EditIdentity/${id}`,
      request
    );
  }

  editPasswordByAdmin(id: string, request: EditPasswordByAdminRequest): Observable<void> {
    return this.put<void, EditPasswordByAdminRequest>(
      `${this.baseUrl}/${id}/EditPasswordByAdminUser`,
      request,
    );
  }

  editPasswordByOwnUser(request: EditPasswordByOwnUserRequest): Observable<void> {
    return this.put<void, EditPasswordByOwnUserRequest>(
      `${this.baseUrl}/EditPasswordByOwnUser`,
      request,
    );
  }

  editTemporalPassword(id: string, request: EditTemporalPasswordRequest): Observable<void> {
    return this.put<void, EditTemporalPasswordRequest>(
      `${this.baseUrl}/${id}/EditTemporalPassword`,
      request,
    );
  }

  forgotPassword(request: { userName: string | null; callback: string | null }): Observable<void> {
    return this.post<void, typeof request>(`${this.baseUrl}/ForgotPassword`, request);
  }

  resetPassword(request: { userName: string | null; code: string | null; password: string | null }): Observable<void> {
    return this.put<void, typeof request>(`${this.baseUrl}/ResetPassword`, request);
  }

  getDetails(id: string): Observable<User> {
    return this.get<UserApiResponse>(`${this.baseUrl}/GetDetails/${id}`).pipe(
      map((response) => UserMapper.toDomain(response))
    );
  }

  getListPaged(request: UserListPagedRequest): Observable<UserListPagedResponse> {
    return this.post<UserListPagedApiResponse, UserListPagedRequest>(
      `${this.baseUrl}/GetListPaged`,
      request
    ).pipe(
      map((response) => ({
        items: UserMapper.toDomainList(response.items),
        pageNumber: response.pageNumber,
        pageSize: response.pageSize,
        totalPages: response.totalPages,
        totalItemsCount: response.totalItemsCount,
        orderField: response.orderField,
        ascendingOrder: response.ascendingOrder,
      }))
    );
  }

  getTechnicalListPaged(request: UserListPagedRequest): Observable<UserListPagedResponse> {
    return this.post<UserListPagedApiResponse, UserListPagedRequest>(
      `${this.baseUrl}/TechnicalUsersGetListPaged`,
      request
    ).pipe(
      map((response) => ({
        items: UserMapper.toDomainList(response.items),
        pageNumber: response.pageNumber,
        pageSize: response.pageSize,
        totalPages: response.totalPages,
        totalItemsCount: response.totalItemsCount,
        orderField: response.orderField,
        ascendingOrder: response.ascendingOrder,
      }))
    );
  }
}
